import React, { useCallback, useMemo, useState } from "react";
import { Box, Container, Alert } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate, useSearchParams } from "react-router";
import { Windpark } from "../../../types";
import { useToast } from "../../../contexts/ToastContext";
import { useHeaderFocus } from "../../../contexts/HeaderContext";
import { apiService } from "../../../services/api";
import { projectsApi } from "../../../services/api/projects";
import { useOverviewData } from "./hooks/useOverviewData";
import { useProjectSignals } from "./hooks/useProjectSignals";
import { usePermitSummary } from "./hooks/usePermitSummary";
import { useUserOptions } from "./hooks/useUserOptions";
import { usePermitSubRows } from "./hooks/usePermitSubRows";
import { permitsApi } from "../../../services/api/permits";
import { errorMessage } from "../../../hooks/useProjectDeadlines";
import PageTemplate from "../../layout/PageTemplate";
import Button from "../../common/inputs/Button";
import ReminderSection, { ReminderFocus } from "./sections/ReminderSection";
import ProjectsSection from "./sections/ProjectsSection";
import OverviewMapSection from "./sections/OverviewMapSection";
import ProjectFocusDetail from "./sections/ProjectFocusDetail";
import Section from "../../layout/Section";
import {
  editsPermitProcedures,
  permitsRunning,
  seesAllProjectDeadlines,
  seesPermitProcedures,
} from "../../../utils/projectFocus";

/**
 * Die Startseite ist ein fester Arbeitsbereich für den Jour fixe: Karte und Termine stehen oben,
 * die Windparks scrollen darunter in einem eigenen Bereich. Ein Klick auf eine Zeile wählt das
 * Projekt aus und klappt es auf; Karte und Termine zeigen dann nur noch dieses Projekt, ↑/↓
 * wechselt, Esc hebt die Auswahl auf. Das ausgewählte Projekt steht im Header hinter
 * „strømcraft“, die übrigen Zeilen treten zurück. Die Auswahl steht in der URL (`?project=`),
 * damit Neuladen und Zurück sie behalten; die erste Auswahl ist ein eigener Eintrag im
 * Verlauf, sodass der Zurück-Knopf des Browsers sie wieder aufhebt.
 */
const TOP_HEIGHT = "42vh";
/** Kopfzeile der App (64 px) und Innenabstand der Seite oben und unten (2 × 24 px) */
const APP_CHROME = "112px";
/**
 * Titel der Kacheln oben (32 px), Abstand zu den Windparks (24 px), Kopf der Kachel „Windparks“
 * (75 px) und etwas Luft (16 px) – gemessen bei 1600 × 1000
 */
const TABLE_CHROME = "147px";
const TABLE_MAX_HEIGHT = `calc(100vh - ${APP_CHROME} - ${TOP_HEIGHT} - ${TABLE_CHROME})`;

const FOCUS_PARAM = "project";

interface OverviewPageProps {
  onProjectCreated: (windpark: Windpark) => void;
}

const OverviewPage: React.FC<OverviewPageProps> = ({ onProjectCreated }) => {
  const { showSuccess, showError } = useToast();
  const { projects, kpis, loading, error, removeProject } = useOverviewData();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Termine sind mandantenweit. Ein Externer gehört keinem Mandanten an, der Abschnitt bliebe
  // für ihn dauerhaft leer – dann zeigen wir ihn gar nicht erst.
  const nurExterneProjekte =
    projects.length > 0 && projects.every((projekt) => !projekt.ist_intern);
  const [exportingProjectId, setExportingProjectId] = useState<string | null>(
    null,
  );

  const focusProjectId = searchParams.get(FOCUS_PARAM);
  const focusProject = useMemo(
    () => projects.find((project) => project.id === focusProjectId) ?? null,
    [projects, focusProjectId],
  );

  const setFocus = useCallback(
    (projectId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (projectId) next.set(FOCUS_PARAM, projectId);
          else next.delete(FOCUS_PARAM);
          return next;
        },
        // Nur der Schritt in ein Projekt kommt in den Verlauf. Wechsel mit ↑/↓ und Aufheben
        // ersetzen den Eintrag, sonst müsste man sich durch jede Zeile zurückklicken.
        { replace: !projectId || !!focusProjectId },
      );
    },
    [setSearchParams, focusProjectId],
  );

  useHeaderFocus(
    focusProject
      ? {
          label: focusProject.name,
          onOpen: () => navigate(`/project/${focusProject.id}/overview`),
          onClear: () => setFocus(null),
        }
      : null,
    [focusProject?.id, focusProject?.name],
  );

  const { signals, reload: reloadSignals } = useProjectSignals(true);
  const canSeePermits = seesPermitProcedures(focusProject?.meine_rechte);
  const canEditPermits = editsPermitProcedures(focusProject?.meine_rechte);
  const running = !!focusProject && permitsRunning(focusProject);
  const {
    summary,
    failed,
    reload: reloadSummary,
  } = usePermitSummary(focusProject?.id ?? null, canSeePermits && running);
  const users = useUserOptions();

  /** Eine Angabe am Antrag aus der aufgeklappten Zeile speichern, wie in den Terminen */
  const saveProcedure = async (
    procedureId: string,
    data: { genehmigung_geplant_am?: string; bearbeitung_user_id?: string },
  ) => {
    if (!focusProject) return;
    try {
      await permitsApi.updatePermitProcedure(
        focusProject.id,
        procedureId,
        data,
      );
      reloadSummary();
      showSuccess("Gespeichert");
    } catch (err) {
      showError(errorMessage(err, "Antrag konnte nicht gespeichert werden"));
    }
  };

  // Gibt es Anträge zu zeigen, stehen sie als Unterzeilen in der Tabelle; sonst ein Hinweis.
  const hasSubRows =
    running && canSeePermits && !failed && !!summary?.procedures.length;

  /** Antrag, an dem gerade ein Termin angelegt wird – die Kachel „Termine“ öffnet ihren Dialog */
  const [deadlineAt, setDeadlineAt] = useState<string | null>(null);

  const permitSubRows = usePermitSubRows({
    summary: hasSubRows ? summary : null,
    canEdit: canEditPermits,
    users,
    onPlannedApprovalChange: (procedureId, date) =>
      saveProcedure(procedureId, { genehmigung_geplant_am: date }),
    onAssigneeChange: (procedureId, userId) =>
      saveProcedure(procedureId, { bearbeitung_user_id: userId }),
    onAddDeadline: setDeadlineAt,
  });

  const reminderFocus: ReminderFocus | null = useMemo(
    () =>
      focusProject
        ? {
            projectId: focusProject.id,
            seesAll: seesAllProjectDeadlines(focusProject.meine_rechte),
          }
        : null,
    [focusProject],
  );

  const handleProjectDeleted = async (projectId: string) => {
    try {
      await apiService.deleteProject(projectId);
      removeProject(projectId);
      if (projectId === focusProjectId) setFocus(null);
      showSuccess("Projekt wurde erfolgreich gelöscht!");
    } catch {
      showError("Fehler beim Löschen des Projekts");
    }
  };

  const handleProjectExport = async (projectId: string) => {
    setExportingProjectId(projectId);
    try {
      await projectsApi.exportProject(projectId);
    } catch {
      showError("Export fehlgeschlagen. Bitte versuche es erneut.");
    } finally {
      setExportingProjectId(null);
    }
  };

  return (
    <PageTemplate isLoading={loading}>
      <Container maxWidth="xl">
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button
                variant="secondary"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => window.location.reload()}
              >
                Erneut versuchen
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: nurExterneProjekte
              ? "1fr"
              : // `minmax(0, …)`: Sonst wird eine Spalte nie schmaler als ihr längster
                // einzeiliger Inhalt, und lange Terminbezeichnungen drückten die Karte zusammen.
                { xs: "minmax(0, 1fr)", md: "minmax(0, 3fr) minmax(0, 2fr)" },
            gap: 3,
            alignItems: "start",
            mb: 3,
          }}
        >
          <Section title="Karte" noPadding={true} sx={{ overflow: "hidden" }}>
            <OverviewMapSection
              projects={projects}
              onProjectSelect={setFocus}
              selectedProjectId={focusProjectId ?? undefined}
              focusProjectId={focusProjectId}
              height={TOP_HEIGHT}
            />
          </Section>
          {!nurExterneProjekte && (
            <ReminderSection
              sx={{ height: TOP_HEIGHT }}
              focus={reminderFocus}
              onChanged={reloadSignals}
              createAtProcedure={deadlineAt}
              onCreateClosed={() => setDeadlineAt(null)}
            />
          )}
        </Box>

        <ProjectsSection
          projects={projects}
          onProjectDeleted={handleProjectDeleted}
          onProjectExport={handleProjectExport}
          exportingProjectId={exportingProjectId}
          onProjectCreated={onProjectCreated}
          wirtschaftlichkeitErklaerung={kpis.wirtschaftlichkeitErklaerung}
          reifegradErklaerung={kpis.reifegradErklaerung}
          reifegradGewichte={kpis.reifegradGewichte}
          focusProjectId={focusProjectId}
          onFocusChange={setFocus}
          // Ein Hinweis nur am Projekt im Fokus – die übrigen Zeilen sind zugeklappt
          renderFocusDetail={(projectId) =>
            projectId === focusProjectId && !hasSubRows ? (
              <ProjectFocusDetail
                projectId={projectId}
                permitsRunning={running}
                canSeePermits={canSeePermits}
                summary={summary}
                failed={failed}
              />
            ) : null
          }
          focusSubRows={(projectId) =>
            projectId === focusProjectId ? permitSubRows(projectId) : []
          }
          signals={signals}
          tableMaxHeight={TABLE_MAX_HEIGHT}
        />
      </Container>
    </PageTemplate>
  );
};

export default OverviewPage;
